import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  FileText,
  FolderX,
  Link2,
  ListTodo,
  Loader2,
  LogOut,
  MapPin,
  Search,
  UploadCloud,
} from "lucide-react";
import { toast } from "sonner";
import { base44 } from "@/api/base44Client";
import { useActionstepSession } from "@/hooks/useActionstepSession";
import { COURT_OPTIONS, withCurrentValue } from "@/lib/formChoices";
import { buildActionstepAuthorizeUrl,
  ACTIONSTEP_SCOPES,
  DEFAULT_ACTIONSTEP_ENVIRONMENT,
} from "@/lib/aidPlannerAdapters/actionstepClient";
import {
  buildActionstepOutcomeNote,
  buildMicrosoftCalendarEvent,
  createAppearanceSnapshot,
  createNextAppearanceSnapshot,
  readMicrosoftAppearance,
} from "@/lib/outlookAppearanceFormat";
import {
  saveDemoActionstepOutcome,
  searchDemoActionstepMatters,
} from "@/lib/outlookActionstepDemo";
import {
  isAppointmentCompose,
  isOutlookAppointment,
  loadOutlookAppointment,
  openNextOutlookAppointment,
  saveOutlookAppearanceMetadata,
  saveOutlookAppointment,
} from "@/lib/outlookAppointmentClient";
import {
  CLS_CONNECT_GOOGLE_CLIENT_ID,
  createGoogleCalendarEvent,
  listGoogleCalendarEvents,
  listGoogleCalendars,
  requestGoogleCalendarAccessToken,
  updateGoogleCalendarEvent,
} from "@/lib/googleCalendarClient";
import {
  createOutlookCalendarEvent,
  listOutlookCalendars,
  requestMicrosoftCalendarAccessToken,
  updateOutlookCalendarEvent,
} from "@/lib/microsoftCalendarClient";
import {
  buildGoogleCalendarEvent,
  formatGoogleEventOption,
  readGoogleCalendarAppearance,
} from "@/lib/googleCalendarAppearance";

import {
  buildLacwPortalRequest,
  saveDemoPortalRequest,
} from "@/lib/outlookPortalRequests";
import {
  buildCounselListEmail,
  buildLacwBacksheetUrl,
  openCounselListEmail,
  openPortalWindow,
} from "@/lib/outlookBriefing";
import {
  buildLacwBillingDraft,
  saveLocalOutlookBillingDraft,
  upsertOutlookBillingDraft,
} from "@/lib/outlookBillingBridge";

const APPEARANCE_TYPES = [
  "Mention",
  "Contest Mention",
  "Bail Application",
  "Plea",
  "Sentence",
  "Committal",
  "Trial",
  "Appeal",
  "Conference",
  "Other",
];
const FUNDING_TYPES = ["Legal Aid", "LACW", "Private", "Pro Bono"];
const FOLLOW_UP_TYPES = [
  { id: "next_court_date", label: "Next court date", icon: CalendarDays },
  { id: "action_item", label: "Action item", icon: ListTodo },
  { id: "close_file", label: "Close file", icon: FolderX },
];
const DOCUMENT_TYPES = ["Client letter", "File note", "Other"];

const DEFAULT_FIELDS = {
  date: "",
  startTime: "09:00",
  endTime: "10:00",
  court: "",
  listingLocation: "",
  fundingType: "LACW",
  grantOfAidNumber: "",
  matterToBeBriefed: false,
  counselListMethod: "email_clerk",
  clerkEmail: "",
  briefingAssigneeRole: "Paralegal",
  briefingNotes: "",
  nextCourtDate: "",
  nextCourtTime: "09:00",
  nextAppearanceType: "Mention",
  appearanceType: "Mention",
  outcome: "",
  notes: "",
};

const ENV_CLIENT_ID = import.meta.env.VITE_ACTIONSTEP_CLIENT_ID || "";
const ENV_CLIENT_SECRET = import.meta.env.VITE_ACTIONSTEP_CLIENT_SECRET || "";
const ENV_REDIRECT_URI = import.meta.env.VITE_ACTIONSTEP_REDIRECT_URI || "";
// Falls back to DEFAULT_ACTIONSTEP_ENVIRONMENT ("staging"), the same fallback every other
// Actionstep screen uses. This pane used to fall back to "production" instead, so with the
// variable unset it sent a staging client ID to the live Actionstep server, which answered
// "invalid_client" - while the document tools, on staging, connected fine with the same ID.
// Two defaults for one integration is a difference nobody can see from the screen.
const ENV_ENVIRONMENT =
  import.meta.env.VITE_ACTIONSTEP_ENVIRONMENT || DEFAULT_ACTIONSTEP_ENVIRONMENT;
const INPUT_CLASS =
  "min-w-0 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-900 shadow-sm outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-100";

function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
        {label}
      </span>
      {children}
    </label>
  );
}

export default function OutlookAppearancePane({
  mobileMode = false,
  portalMode = false,
  portalMicrosoftSession = null,
  portalEvent = null,
  portalComposeEmail = null,
  // When the host draws the save button it registers here, and this pane stops drawing its
  // own. Two buttons each wrote their own calendar entry, so an appointment filled in on
  // both halves of the screen arrived in Outlook twice.
  portalRegisterSave = null,
  // Reports the listing back to whatever is hosting this pane, so an appointment above it
  // can fill itself in rather than asking for the same details a second time.
  portalOnListingChange = null,
}) {
  const actionstep = useActionstepSession();
  const [officeItem, setOfficeItem] = useState(null);
  const [hasCalendarEvent, setHasCalendarEvent] = useState(true);
  const [isCompose, setIsCompose] = useState(true);
  const [selectedMatter, setSelectedMatter] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [fields, setFields] = useState(DEFAULT_FIELDS);
  const [isLoadingAppointment, setIsLoadingAppointment] = useState(true);
  // Reopening an appointment that already carries its matter and listing should not ask
  // for either again - that is the same information typed a second time. They collapse to
  // a summary rather than disappearing: a wrong court or a changed matter still has to be
  // fixable, and a box that cannot be reached is worse than one that is merely in the way.
  const [showMatterSection, setShowMatterSection] = useState(false);
  const [showListingSection, setShowListingSection] = useState(false);
  const [isSavingCalendar, setIsSavingCalendar] = useState(false);
  const [calendarSaved, setCalendarSaved] = useState(false);
  const [outcomeSaved, setOutcomeSaved] = useState(false);
  const [clientId, setClientId] = useState(ENV_CLIENT_ID);
  const [clientSecret, setClientSecret] = useState(ENV_CLIENT_SECRET);
  const [isOpeningActionstep, setIsOpeningActionstep] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoSavedNote, setDemoSavedNote] = useState("");
  const [followUpType, setFollowUpType] = useState("next_court_date");
  const [isSavingNextCourt, setIsSavingNextCourt] = useState(false);
  const [nextCourtSaved, setNextCourtSaved] = useState(false);
  const [actionItem, setActionItem] = useState("");
  const [actionPriority, setActionPriority] = useState("Normal");
  const [fileOutcome, setFileOutcome] = useState("");
  const [documentType, setDocumentType] = useState("Client letter");
  const [documentInstructions, setDocumentInstructions] = useState("");
  const [isSubmittingPortalRequest, setIsSubmittingPortalRequest] =
    useState(false);
  const [portalRequestReceipt, setPortalRequestReceipt] = useState(null);
  const [isSendingToBilling, setIsSendingToBilling] = useState(false);
  const [billingQueued, setBillingQueued] = useState(false);
  const [materialFiles, setMaterialFiles] = useState([]);
  const [uploadedMaterials, setUploadedMaterials] = useState([]);
  const [outcomeExpanded, setOutcomeExpanded] = useState(false);
  const [googleClientId, setGoogleClientId] = useState(
    () =>
      window.sessionStorage.getItem("clsConnectGoogleClientId") ||
      CLS_CONNECT_GOOGLE_CLIENT_ID,
  );
  const [googleAccessToken, setGoogleAccessToken] = useState(
    () => window.sessionStorage.getItem("clsConnectGoogleAccessToken") || "",
  );
  const [googleCalendars, setGoogleCalendars] = useState(() => {
    try {
      return JSON.parse(
        window.sessionStorage.getItem("clsConnectGoogleCalendars") || "[]",
      );
    } catch {
      return [];
    }
  });
  const [selectedGoogleCalendarId, setSelectedGoogleCalendarId] = useState(
    () => window.sessionStorage.getItem("clsConnectGoogleCalendarId") || "",
  );
  const [googleEvents, setGoogleEvents] = useState([]);
  const [selectedGoogleEventId, setSelectedGoogleEventId] = useState("");
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false);
  const [isLoadingGoogleEvents, setIsLoadingGoogleEvents] = useState(false);
  const taskPaneParams = new URLSearchParams(window.location.search);
  const requestedCalendarProvider = taskPaneParams.get("provider");
  const [calendarProvider, setCalendarProvider] = useState(
    () =>
      (portalMode
        ? "microsoft"
        : window.sessionStorage.getItem("clsConnectCalendarProvider")) ||
      (requestedCalendarProvider === "microsoft" ? "microsoft" : "google"),
  );
  const [microsoftAccessToken, setMicrosoftAccessToken] = useState(
    () =>
      portalMicrosoftSession?.accessToken ||
      window.sessionStorage.getItem("clsConnectMicrosoftAccessToken") ||
      "",
  );
  const [microsoftCalendars, setMicrosoftCalendars] = useState(() => {
    if (portalMicrosoftSession?.calendars?.length)
      return portalMicrosoftSession.calendars;
    try {
      return JSON.parse(
        window.sessionStorage.getItem("clsConnectMicrosoftCalendars") || "[]",
      );
    } catch {
      return [];
    }
  });
  const [selectedMicrosoftCalendarId, setSelectedMicrosoftCalendarId] =
    useState(
      () =>
        portalMicrosoftSession?.calendarId ||
        window.sessionStorage.getItem("clsConnectMicrosoftCalendarId") ||
        "",
    );
  const [isConnectingMicrosoft, setIsConnectingMicrosoft] = useState(false);
  const isNativeRuntime =
    window.location.protocol === "capacitor:" ||
    window.Capacitor?.isNativePlatform?.() === true;
  const isStandaloneGoogle =
    calendarProvider === "google" &&
    (requestedCalendarProvider === "google" ||
      (requestedCalendarProvider !== "microsoft" &&
        taskPaneParams.get("mode") === "appointment") ||
      (!officeItem && !window.Office?.context?.mailbox));

  const appearance = useMemo(
    () => createAppearanceSnapshot(selectedMatter, fields),
    [selectedMatter, fields],
  );
  const isExistingCalendarEntry = Boolean(
    selectedGoogleEventId || officeItem?.itemId || portalEvent?.id,
  );

  // An appointment that already carries its own matter does not need to be asked again.
  const alreadyLinked = isExistingCalendarEntry && Boolean(selectedMatter);

  useEffect(() => {
    if (!portalOnListingChange) return;
    portalOnListingChange({
      matterId: appearance.actionstepMatterId,
      clientName: appearance.clientName,
      fileNumber: appearance.fileNumber,
      appearanceType: appearance.appearanceType,
      court: appearance.court,
      listingLocation: appearance.listingLocation,
      date: appearance.date,
      startTime: appearance.startTime,
      endTime: appearance.endTime,
    });
  }, [appearance, portalOnListingChange]);

  useEffect(() => {
    if (!portalMode || !portalMicrosoftSession?.accessToken) return;
    setMicrosoftAccessToken(portalMicrosoftSession.accessToken);
    setMicrosoftCalendars(portalMicrosoftSession.calendars || []);
    setSelectedMicrosoftCalendarId(portalMicrosoftSession.calendarId || "");
    setCalendarProvider("microsoft");
  }, [portalMode, portalMicrosoftSession]);

  useEffect(() => {
    if (!portalMode || !portalEvent) return;

    // What this appointment already knows about itself. An appointment saved from here
    // carries its own appearance, so reopening it should not ask for the matter again.
    const stored = readMicrosoftAppearance(portalEvent);

    setFields((current) => ({
      ...current,
      ...(stored || {}),
      date: portalEvent.start?.dateTime?.slice(0, 10) || stored?.date || current.date,
      startTime:
        portalEvent.start?.dateTime?.slice(11, 16) || stored?.startTime || current.startTime,
      endTime:
        portalEvent.end?.dateTime?.slice(11, 16) || stored?.endTime || current.endTime,
      listingLocation:
        stored?.listingLocation ||
        portalEvent.location?.displayName ||
        current.listingLocation,
    }));

    if (stored?.actionstepMatterId || stored?.clientName) {
      // Rebuilt from the snapshot rather than refetched: the appointment is the record of
      // what it was linked to, and a lookup here would fail whenever Actionstep is not
      // connected yet - which is exactly when someone is opening an old appointment.
      setSelectedMatter({
        externalId: stored.actionstepMatterId || "",
        client: {
          fullName: stored.clientName || "",
          fileNumber: stored.fileNumber || "",
        },
        matter: {
          court: stored.court || "",
          appearanceType: stored.appearanceType || "Mention",
          fundingType: stored.fundingType || "LACW",
          grantOfAidNumber: stored.grantOfAidNumber || "",
        },
      });
    }

    setIsCompose(false);
    setOutcomeExpanded(true);
  }, [portalEvent, portalMode]);

  useEffect(() => {
    if (isExistingCalendarEntry) setOutcomeExpanded(true);
  }, [isExistingCalendarEntry]);

  useEffect(() => {
    const initialize = async () => {
      setIsLoadingAppointment(true);
      setBillingQueued(false);
      const mailbox = window.Office?.context?.mailbox;
      const item = mailbox?.item || null;
      if (mailbox && !isOutlookAppointment(item)) {
        setOfficeItem(null);
        setHasCalendarEvent(false);
        setSelectedMatter(null);
        setFields(DEFAULT_FIELDS);
        setIsLoadingAppointment(false);
        return;
      }

      setHasCalendarEvent(true);
      setOfficeItem(item);
      setIsCompose(item ? isAppointmentCompose(item) : true);

      try {
        const appointment = item ? await loadOutlookAppointment(item) : null;
        const stored = appointment?.storedAppearance;
        if (stored) {
          setSelectedMatter(stored.matter || null);
          setFields({
            date: appointment.date || stored.date || "",
            startTime: appointment.startTime || stored.startTime || "09:00",
            endTime: appointment.endTime || stored.endTime || "10:00",
            court: stored.court || "",
            listingLocation:
              stored.listingLocation || appointment.location || "",
            fundingType: stored.fundingType || "LACW",
            grantOfAidNumber: stored.grantOfAidNumber || "",
            matterToBeBriefed: Boolean(stored.matterToBeBriefed),
            counselListMethod: stored.counselListMethod || "email_clerk",
            clerkEmail: stored.clerkEmail || "",
            briefingAssigneeRole: stored.briefingAssigneeRole || "Paralegal",
            briefingNotes: stored.briefingNotes || "",
            nextCourtDate: stored.nextCourtDate || "",
            nextCourtTime: stored.nextCourtTime || "09:00",
            nextAppearanceType: stored.nextAppearanceType || "Mention",
            appearanceType: stored.appearanceType || "Mention",
            outcome: stored.outcome || "",
            notes: stored.notes || "",
          });
        } else if (appointment) {
          setFields((current) => ({
            ...current,
            date: appointment.date || current.date,
            startTime: appointment.startTime || current.startTime,
            endTime: appointment.endTime || current.endTime,
            listingLocation: appointment.location || current.listingLocation,
          }));
        }
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Could not read this appointment.",
        );
      } finally {
        setIsLoadingAppointment(false);
      }
    };

    const handleItemChanged = () => {
      initialize();
    };
    let mailboxWithHandler = null;

    if (window.Office) {
      window.Office.onReady(() => {
        initialize();
        mailboxWithHandler = window.Office?.context?.mailbox || null;
        if (
          mailboxWithHandler?.addHandlerAsync &&
          window.Office?.EventType?.ItemChanged
        ) {
          mailboxWithHandler.addHandlerAsync(
            window.Office.EventType.ItemChanged,
            handleItemChanged,
          );
        }
      });
    } else {
      setFields((current) => ({
        ...current,
        date: new Date().toISOString().slice(0, 10),
      }));
      setIsLoadingAppointment(false);
    }

    return () => {
      if (
        mailboxWithHandler?.removeHandlerAsync &&
        window.Office?.EventType?.ItemChanged
      ) {
        mailboxWithHandler.removeHandlerAsync(
          window.Office.EventType.ItemChanged,
          { handler: handleItemChanged },
        );
      }
    };
  }, []);

  const updateField = (name, value) => {
    setFields((current) => ({ ...current, [name]: value }));
    setCalendarSaved(false);
    setBillingQueued(false);
    if (name === "outcome" || name === "notes") {
      setOutcomeSaved(false);
      setDemoSavedNote("");
    }
    if (
      name === "nextCourtDate" ||
      name === "nextCourtTime" ||
      name === "nextAppearanceType"
    ) {
      setNextCourtSaved(false);
    }
  };

  const selectMatter = (matter) => {
    setSelectedMatter(matter);
    setQuery("");
    setResults([]);
    setCalendarSaved(false);
    setOutcomeSaved(false);
    setDemoSavedNote("");
    setFields((current) => ({
      ...current,
      date: matter.matter?.nextAppearanceDate || current.date,
      court: matter.matter?.court || current.court,
      appearanceType: matter.matter?.appearanceType || current.appearanceType,
      fundingType: matter.matter?.fundingType || current.fundingType,
      grantOfAidNumber:
        matter.matter?.grantOfAidNumber || current.grantOfAidNumber,
    }));
    setPortalRequestReceipt(null);
    setBillingQueued(false);
    setMaterialFiles([]);
    setUploadedMaterials([]);
  };

  const searchMatters = async (event) => {
    event.preventDefault();
    if (query.trim().length < 2) {
      toast.error("Enter at least two characters.");
      return;
    }
    try {
      setResults(
        isDemoMode
          ? searchDemoActionstepMatters(query)
          : await actionstep.searchMatters(query),
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Actionstep search failed.",
      );
    }
  };

  const connectActionstep = () => {
    if (isNativeRuntime) {
      toast.error(
        "Native Actionstep sign-in is not configured in this prototype. Use Demo Mode for device testing.",
      );
      return;
    }
    const redirectUri = `${window.location.origin}/outlook-actionstep-auth.html`;
    const officeUi = window.Office?.context?.ui;

    if (!officeUi?.displayDialogAsync) {
      actionstep.connect({
        clientId,
        clientSecret,
        redirectUri: ENV_REDIRECT_URI || redirectUri,
        environment: ENV_ENVIRONMENT,
      });
      return;
    }

    const authorizeUrl = buildActionstepAuthorizeUrl({
      environment: ENV_ENVIRONMENT,
      clientId,
      scopes: ACTIONSTEP_SCOPES,
      redirectUri,
    });
    const dialogUrl = new URL(
      "/outlook-actionstep-auth.html",
      window.location.origin,
    );
    dialogUrl.searchParams.set("authorizeUrl", authorizeUrl);
    setIsOpeningActionstep(true);

    officeUi.displayDialogAsync(
      dialogUrl.toString(),
      { height: 70, width: 45, displayInIframe: false },
      (result) => {
        if (result.status !== window.Office.AsyncResultStatus.Succeeded) {
          setIsOpeningActionstep(false);
          toast.error(
            result.error?.message ||
              "Outlook could not open Actionstep sign-in.",
          );
          return;
        }

        const dialog = result.value;
        const closeDialog = () => {
          try {
            dialog.close();
          } catch {
            // The user may already have closed the dialog.
          }
          setIsOpeningActionstep(false);
        };

        dialog.addEventHandler(
          window.Office.EventType.DialogMessageReceived,
          async (event) => {
            try {
              const message = JSON.parse(event.message || "{}");
              if (message.type !== "actionstep-oauth") return;
              if (message.error) throw new Error(message.error);
              if (!message.code)
                throw new Error(
                  "Actionstep did not return an authorization code.",
                );

              await actionstep.exchangeCode({
                clientId,
                clientSecret,
                redirectUri,
                environment: ENV_ENVIRONMENT,
                code: message.code,
              });
              closeDialog();
              toast.success("Actionstep connected.");
            } catch (error) {
              closeDialog();
              toast.error(
                error instanceof Error
                  ? error.message
                  : "Actionstep sign-in failed.",
              );
            }
          },
        );

        dialog.addEventHandler(
          window.Office.EventType.DialogEventReceived,
          (event) => {
            closeDialog();
            if (event.error && event.error !== 12006) {
              toast.error(
                `Actionstep sign-in window closed with Outlook error ${event.error}.`,
              );
            }
          },
        );
      },
    );
  };

  const startDemoMode = () => {
    setIsDemoMode(true);
    setSelectedMatter(null);
    setResults([]);
    setQuery("");
    setOutcomeSaved(false);
    setDemoSavedNote("");
    setBillingQueued(false);
    toast.success("Demo Mode started. No Actionstep data will be accessed.");
  };

  const disconnectMatterSource = () => {
    if (isDemoMode) {
      setIsDemoMode(false);
      setSelectedMatter(null);
      setResults([]);
      setOutcomeSaved(false);
      setDemoSavedNote("");
      toast.success("Demo Mode ended.");
      return;
    }
    actionstep.disconnect();
  };

  const googleDateWindow = () => {
    const from = new Date();
    const to = new Date();
    from.setDate(from.getDate() - 90);
    to.setDate(to.getDate() + 365);
    return {
      from: from.toISOString().slice(0, 10),
      to: to.toISOString().slice(0, 10),
    };
  };

  const loadConnectedGoogleEvents = async (token, calendarId) => {
    if (!token || !calendarId) return [];
    setIsLoadingGoogleEvents(true);
    try {
      const range = googleDateWindow();
      const events = await listGoogleCalendarEvents(
        token,
        calendarId,
        range.from,
        range.to,
      );
      setGoogleEvents(events);
      return events;
    } finally {
      setIsLoadingGoogleEvents(false);
    }
  };

  const connectGoogleCalendar = async () => {
    if (isNativeRuntime) {
      toast.error(
        "Native Google sign-in requires the Android/iOS OAuth registration. Use Demo Mode for this prototype.",
      );
      return;
    }
    const configuredClientId = googleClientId.trim();
    if (!configuredClientId) {
      toast.error("Enter the Google OAuth client ID for CLS Connect.");
      return;
    }

    setIsConnectingGoogle(true);
    try {
      const token = await requestGoogleCalendarAccessToken(configuredClientId);
      const calendars = await listGoogleCalendars(token);
      if (!calendars.length)
        throw new Error("No editable Google Calendars were found.");
      const calendarId =
        calendars.find((calendar) => calendar.primary)?.id || calendars[0].id;
      window.sessionStorage.setItem(
        "clsConnectGoogleClientId",
        configuredClientId,
      );
      window.sessionStorage.setItem("clsConnectGoogleAccessToken", token);
      window.sessionStorage.setItem(
        "clsConnectGoogleCalendars",
        JSON.stringify(calendars),
      );
      window.sessionStorage.setItem("clsConnectGoogleCalendarId", calendarId);
      setGoogleClientId(configuredClientId);
      setGoogleAccessToken(token);
      setGoogleCalendars(calendars);
      setSelectedGoogleCalendarId(calendarId);
      setCalendarProvider("google");
      window.sessionStorage.setItem("clsConnectCalendarProvider", "google");
      await loadConnectedGoogleEvents(token, calendarId);
      toast.success("Google Calendar connected.");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not connect Google Calendar.",
      );
    } finally {
      setIsConnectingGoogle(false);
    }
  };

  const connectMicrosoftCalendar = async () => {
    if (isNativeRuntime) {
      toast.error(
        "Native Microsoft sign-in requires an application callback link. Use Demo Mode for this prototype.",
      );
      return;
    }
    setIsConnectingMicrosoft(true);
    try {
      const token = await requestMicrosoftCalendarAccessToken();
      const calendars = await listOutlookCalendars(token);
      if (!calendars.length)
        throw new Error(
          "No Outlook calendars were available for this account.",
        );
      const calendarId =
        calendars.find((calendar) => calendar.primary)?.id || calendars[0].id;
      window.sessionStorage.setItem("clsConnectMicrosoftAccessToken", token);
      window.sessionStorage.setItem(
        "clsConnectMicrosoftCalendars",
        JSON.stringify(calendars),
      );
      window.sessionStorage.setItem(
        "clsConnectMicrosoftCalendarId",
        calendarId,
      );
      window.sessionStorage.setItem("clsConnectCalendarProvider", "microsoft");
      setMicrosoftAccessToken(token);
      setMicrosoftCalendars(calendars);
      setSelectedMicrosoftCalendarId(calendarId);
      setCalendarProvider("microsoft");
      toast.success("Outlook Calendar connected.");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not connect Outlook Calendar.",
      );
    } finally {
      setIsConnectingMicrosoft(false);
    }
  };

  const disconnectMicrosoftCalendar = () => {
    [
      "clsConnectMicrosoftAccessToken",
      "clsConnectMicrosoftCalendars",
      "clsConnectMicrosoftCalendarId",
    ].forEach((key) => window.sessionStorage.removeItem(key));
    setMicrosoftAccessToken("");
    setMicrosoftCalendars([]);
    setSelectedMicrosoftCalendarId("");
    if (calendarProvider === "microsoft") {
      setCalendarProvider("google");
      window.sessionStorage.setItem("clsConnectCalendarProvider", "google");
    }
    toast.success("Outlook Calendar disconnected.");
  };

  const selectCalendarProvider = (provider) => {
    setCalendarProvider(provider);
    window.sessionStorage.setItem("clsConnectCalendarProvider", provider);
  };

  const disconnectGoogleCalendar = () => {
    [
      "clsConnectGoogleAccessToken",
      "clsConnectGoogleCalendars",
      "clsConnectGoogleCalendarId",
    ].forEach((key) => window.sessionStorage.removeItem(key));
    setGoogleAccessToken("");
    setGoogleCalendars([]);
    setSelectedGoogleCalendarId("");
    setGoogleEvents([]);
    setSelectedGoogleEventId("");
    toast.success("Google Calendar disconnected.");
  };

  const selectGoogleEvent = (eventId) => {
    setSelectedGoogleEventId(eventId);
    setCalendarSaved(false);
    const event = googleEvents.find((item) => item.id === eventId);
    if (!event) return;

    const stored = readGoogleCalendarAppearance(event);
    setFields((current) => ({ ...current, ...stored }));
    if (stored.actionstepMatterId || stored.clientName) {
      setSelectedMatter({
        externalId: stored.actionstepMatterId || "",
        client: {
          fullName: stored.clientName || "",
          fileNumber: stored.fileNumber || "",
        },
        matter: {
          court: stored.court || "",
          appearanceType: stored.appearanceType || "Mention",
          fundingType: stored.fundingType || "LACW",
          grantOfAidNumber: stored.grantOfAidNumber || "",
        },
      });
    }
  };

  const saveCalendar = async () => {
    if (!selectedMatter) {
      toast.error("Select an Actionstep matter first.");
      return;
    }
    if (!fields.date || !fields.appearanceType || !fields.court) {
      toast.error("Date, appearance type, and court are required.");
      return;
    }

    setIsSavingCalendar(true);
    try {
      if (portalMode) {
        if (!microsoftAccessToken || !selectedMicrosoftCalendarId) {
          throw new Error("Connect Outlook before saving this appearance.");
        }
        const generatedPayload = buildMicrosoftCalendarEvent(appearance);
        let savedEvent;
        if (portalEvent?.id) {
          savedEvent = await updateOutlookCalendarEvent(
            microsoftAccessToken,
            selectedMicrosoftCalendarId,
            portalEvent.id,
            {
              ...generatedPayload,
              start: portalEvent.start || generatedPayload.start,
              end: portalEvent.end || generatedPayload.end,
            },
          );
        } else {
          savedEvent = await createOutlookCalendarEvent(
            microsoftAccessToken,
            selectedMicrosoftCalendarId,
            generatedPayload,
          );
        }
        setCalendarSaved(true);
        try {
          const lawyerName = window.Office?.context?.mailbox?.userProfile?.displayName || "";
          const draft = buildLacwBillingDraft(appearance, {
            lawyerName,
            sourceId: savedEvent?.id || portalEvent?.id || appearance.actionstepMatterId,
          });
          await upsertOutlookBillingDraft(draft);
          setBillingQueued(true);
        } catch (billingError) {
          console.warn("Appearance saved but billing intake sync failed:", billingError);
          toast.warning("The appointment was saved, but Billing could not be updated automatically. Try saving the appearance again.");
        }
        toast.success(
          portalEvent?.id
            ? "Outlook appointment updated through CLS Connect."
            : "Outlook appointment created through CLS Connect.",
        );
      } else if (isStandaloneGoogle) {
        if (!googleAccessToken || !selectedGoogleCalendarId) {
          throw new Error(
            "Connect Google Calendar before saving this appearance.",
          );
        }
        const existingEvent =
          googleEvents.find((item) => item.id === selectedGoogleEventId) || {};
        const generatedPayload = buildGoogleCalendarEvent(
          appearance,
          existingEvent,
        );
        const payload = selectedGoogleEventId
          ? {
              ...generatedPayload,
              start: existingEvent.start || generatedPayload.start,
              end: existingEvent.end || generatedPayload.end,
            }
          : generatedPayload;
        const savedEvent = selectedGoogleEventId
          ? await updateGoogleCalendarEvent(
              googleAccessToken,
              selectedGoogleCalendarId,
              selectedGoogleEventId,
              payload,
            )
          : await createGoogleCalendarEvent(
              googleAccessToken,
              selectedGoogleCalendarId,
              payload,
            );
        setGoogleEvents((current) => [
          savedEvent,
          ...current.filter((item) => item.id !== savedEvent.id),
        ]);
        setSelectedGoogleEventId(savedEvent.id);
        setCalendarSaved(true);
        toast.success(
          selectedGoogleEventId
            ? "Google Calendar event updated."
            : "Google Calendar event created.",
        );
      } else {
        const result = await saveOutlookAppointment(officeItem, appearance, {
          preserveDateTime: isExistingCalendarEntry,
        });
        setCalendarSaved(true);
        toast.success(
          result.mock
            ? "Appointment preview saved."
            : "Outlook appointment updated.",
        );
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not update the calendar.",
      );
    } finally {
      setIsSavingCalendar(false);
    }
  };

  // Hand the save to the host when it wants it, refreshed every render: saveCalendar closes
  // over the matter and the fields, so a stale copy would save an older appearance.
  useEffect(() => {
    if (!portalRegisterSave) return undefined;
    portalRegisterSave(saveCalendar);
    return () => portalRegisterSave(null);
  });


  const saveOutcomeToActionstep = async () => {
    if (!selectedMatter?.externalId) {
      toast.error("Select an Actionstep matter first.");
      return;
    }
    if (!fields.outcome.trim()) {
      toast.error("Enter the appearance outcome first.");
      return;
    }

    try {
      const note = buildActionstepOutcomeNote(appearance);
      if (isDemoMode) {
        saveDemoActionstepOutcome({
          matterId: selectedMatter.externalId,
          note,
        });
        setDemoSavedNote(note);
      } else {
        await actionstep.saveFileNote(selectedMatter.externalId, note);
      }
      setOutcomeSaved(true);
      toast.success(
        isDemoMode
          ? "Demo outcome saved for this session only."
          : "Outcome saved to the Actionstep matter.",
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not save the Actionstep note.",
      );
    }
  };

  const uploadMaterialsToActionstep = async () => {
    if (!selectedMatter?.externalId) {
      toast.error("Select an Actionstep matter first.");
      return;
    }
    if (isDemoMode || !actionstep.isConnected) {
      toast.error(
        "Connect Actionstep to upload documents to the client matter.",
      );
      return;
    }
    if (!materialFiles.length) {
      toast.error("Choose one or more files first.");
      return;
    }

    try {
      const completed = [];
      for (const file of materialFiles) {
        await actionstep.uploadMatterDocument(selectedMatter.externalId, file);
        completed.push(file.name);
      }
      setUploadedMaterials((current) => [...current, ...completed]);
      setMaterialFiles([]);
      toast.success(
        `${completed.length} ${completed.length === 1 ? "document" : "documents"} saved to the Actionstep matter.`,
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not upload the selected document.",
      );
    }
  };

  const createNextCourtAppointment = async () => {
    if (!selectedMatter) {
      toast.error("Select a matter first.");
      return;
    }
    if (!fields.nextCourtDate) {
      toast.error("Enter the next court date.");
      return;
    }

    setIsSavingNextCourt(true);
    try {
      const [startHour, startMinute] = fields.nextCourtTime
        .split(":")
        .map(Number);
      const endMinutes = (startHour * 60 + startMinute + 60) % (24 * 60);
      const nextEndTime = `${`${Math.floor(endMinutes / 60)}`.padStart(2, "0")}:${`${endMinutes % 60}`.padStart(2, "0")}`;
      const nextAppearance = createNextAppearanceSnapshot(appearance, {
        date: fields.nextCourtDate,
        startTime: fields.nextCourtTime,
        endTime: nextEndTime,
        appearanceType: fields.nextAppearanceType,
      });
      if (isStandaloneGoogle) {
        if (!googleAccessToken || !selectedGoogleCalendarId) {
          throw new Error(
            "Connect Google Calendar before creating the next court event.",
          );
        }
        const payload = buildGoogleCalendarEvent(nextAppearance);
        let savedEvent;
        if (selectedGoogleEventId) {
          const existingEvent =
            googleEvents.find((item) => item.id === selectedGoogleEventId) ||
            {};
          const originalPayload = buildGoogleCalendarEvent(
            appearance,
            existingEvent,
          );
          const updatedOriginal = await updateGoogleCalendarEvent(
            googleAccessToken,
            selectedGoogleCalendarId,
            selectedGoogleEventId,
            {
              ...originalPayload,
              start: existingEvent.start || originalPayload.start,
              end: existingEvent.end || originalPayload.end,
            },
          );
          setGoogleEvents((current) =>
            current.map((item) =>
              item.id === updatedOriginal.id ? updatedOriginal : item,
            ),
          );
        }
        savedEvent = await createGoogleCalendarEvent(
          googleAccessToken,
          selectedGoogleCalendarId,
          payload,
        );
        setGoogleEvents((current) => [
          savedEvent,
          ...current.filter((item) => item.id !== savedEvent.id),
        ]);
        setNextCourtSaved(true);
        setPortalRequestReceipt({
          simulated: false,
          label:
            "New court event created in Google Calendar. The current appearance remains on its original date.",
        });
        toast.success("Next court event created in Google Calendar.");
        return;
      }

      let result;
      if (officeItem && isCompose) {
        await saveOutlookAppearanceMetadata(officeItem, appearance);
      }
      if (microsoftAccessToken && selectedMicrosoftCalendarId) {
        const savedEvent = await createOutlookCalendarEvent(
          microsoftAccessToken,
          selectedMicrosoftCalendarId,
          buildMicrosoftCalendarEvent(nextAppearance),
        );
        result = { mock: false, automatic: true, eventId: savedEvent.id };
      } else {
        result = openNextOutlookAppointment(
          nextAppearance,
          fields.nextCourtDate,
          fields.nextCourtTime,
        );
      }
      setNextCourtSaved(true);
      setPortalRequestReceipt({
        simulated: result.mock,
        label: result.automatic
          ? `New Outlook event created for ${fields.nextCourtDate}. The current appearance remains on its original date.`
          : result.mock
            ? "Separate appointment preview opened"
            : `Separate Outlook appointment opened for ${fields.nextCourtDate}. Select Save & Close in that appointment.`,
      });
      toast.success(
        result.automatic
          ? "Next court event created in Outlook Calendar."
          : result.mock
            ? "Separate appointment preview opened."
            : "Separate appointment opened. Select Save & Close in the new Outlook appointment.",
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not open the next appointment.",
      );
    } finally {
      setIsSavingNextCourt(false);
    }
  };

  const sendToBillingIntake = async () => {
    if (
      !selectedMatter ||
      !fields.date ||
      !fields.court ||
      !fields.appearanceType
    ) {
      toast.error(
        "Select a matter and complete the date, court, and appearance type first.",
      );
      return;
    }

    setIsSendingToBilling(true);
    try {
      const lawyerName =
        window.Office?.context?.mailbox?.userProfile?.displayName || "";
      const draft = buildLacwBillingDraft(appearance, {
        lawyerName,
        sourceId: officeItem?.itemId || appearance.actionstepMatterId,
      });
      if (isDemoMode) {
        saveLocalOutlookBillingDraft(draft);
      } else {
        await upsertOutlookBillingDraft(draft);
      }
      setBillingQueued(true);
      toast.success(
        isDemoMode
          ? "Demo entry added to the local Outlook Intake queue."
          : "Entry sent to the LACW Outlook Intake queue for review.",
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not send this entry to billing.",
      );
    } finally {
      setIsSendingToBilling(false);
    }
  };

  const submitPortalRequest = async (
    requestKind,
    details,
    selectedDocumentType = "",
  ) => {
    if (!selectedMatter) {
      toast.error("Select a matter first.");
      return;
    }

    setIsSubmittingPortalRequest(true);
    setPortalRequestReceipt(null);
    try {
      const senderEmail =
        window.Office?.context?.mailbox?.userProfile?.emailAddress || "";
      const payload = buildLacwPortalRequest({
        requestKind,
        matter: selectedMatter,
        appearance,
        details,
        documentType: selectedDocumentType,
        priority: requestKind === "action_item" ? actionPriority : "Normal",
        senderEmail,
      });
      const result = isDemoMode
        ? saveDemoPortalRequest(payload)
        : await base44.submissions.create(payload);
      setPortalRequestReceipt({
        simulated: isDemoMode,
        label: isDemoMode
          ? `${payload.subject.replace("[Follow up] ", "")} simulated`
          : `${payload.subject.replace("[Follow up] ", "")} sent to the LACW portal`,
        id: result?.id || result?.submission?.id || "",
      });
      toast.success(
        isDemoMode
          ? "Portal request simulated for this session."
          : "Request sent to the LACW portal.",
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not create the LACW portal request.",
      );
    } finally {
      setIsSubmittingPortalRequest(false);
    }
  };

  const emailClerkForCounsel = () => {
    try {
      const draft = buildCounselListEmail(appearance, fields.clerkEmail);
      if (portalMode && portalComposeEmail) {
        portalComposeEmail(draft);
        toast.success("Clerk email opened in Outlook compose.");
        return;
      }
      const result = openCounselListEmail(draft);
      toast.success(
        result.mock
          ? "Counsel email draft prepared."
          : "New Outlook email opened.",
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not prepare the clerk email.",
      );
    }
  };

  const taskCounselListRequest = () => {
    const role = fields.briefingAssigneeRole || "Paralegal";
    const details = [
      `Please obtain a list of available counsel and allocate this request to a ${role}.`,
      fields.briefingNotes,
    ]
      .filter(Boolean)
      .join("\n\n");
    submitPortalRequest("action_item", details);
  };

  const openBacksheetTool = () => {
    if (!selectedMatter) {
      toast.error("Select a matter first.");
      return;
    }
    const url = buildLacwBacksheetUrl(window.location.origin, appearance);
    openPortalWindow(url);
    toast.success("Opening the LACW Backsheet to Counsel tool.");
  };

  if (isLoadingAppointment) {
    return (
      <main className="grid min-h-screen place-items-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
      </main>
    );
  }

  if (!hasCalendarEvent) {
    return (
      <main className="grid min-h-screen place-items-center bg-slate-50 p-6 text-center text-slate-900">
        <div className="max-w-xs rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <CalendarDays className="mx-auto h-9 w-9 text-purple-700" />
          <h1 className="mt-3 text-base font-bold">Select a calendar event</h1>
          <p className="mt-2 text-xs leading-5 text-slate-600">
            CLS Connect only displays matter and listing details when an Outlook
            calendar appointment is selected or open.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main
      className={`mx-auto w-full max-w-[430px] overflow-x-hidden bg-slate-50 text-slate-900 ${portalMode ? "min-h-0" : "min-h-screen"}`}
    >
      {!portalMode && (
        <header className="border-b border-purple-900 bg-purple-950 px-4 py-3 text-white">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-purple-300">
                Complete Law Support
              </div>
              <h1 className="mt-1 text-base font-bold">
                Actionstep appearance
              </h1>
            </div>
            <CalendarDays className="h-6 w-6 text-purple-300" />
          </div>
          <p className="mt-1.5 text-[11px] leading-4 text-purple-200">
            Link the appointment, listing details and outcome to the client
            matter.
          </p>
        </header>
      )}

      <div className="flex min-w-0 flex-col gap-3 p-3">
        {mobileMode && (
          <section className="min-w-0 break-words rounded-2xl border border-purple-200 bg-purple-50 p-3 text-[11px] leading-5 text-purple-950">
            <strong className="block text-xs">Mobile foundation</strong>
            This build reuses the live CLS Connect workflow in a phone-sized
            surface. Native device builds remain in Demo Mode until the Google,
            Microsoft, and Actionstep app callback registrations are configured.
          </section>
        )}
        {!portalMode && (
          <section className="order-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 px-3.5 py-3">
              <div>
                <h2 className="text-xs font-bold text-slate-900">
                  Calendar connection
                </h2>
                <p className="mt-0.5 text-[10px] text-slate-500">
                  Choose where this appearance is saved.
                </p>
              </div>
              <span className="rounded-full bg-slate-100 px-2 py-1 text-[9px] font-bold uppercase tracking-wide text-slate-600">
                {calendarProvider === "microsoft" ? "Outlook" : "Google"}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-1 border-y border-slate-100 bg-slate-50 p-1.5">
              <button
                type="button"
                onClick={() => selectCalendarProvider("microsoft")}
                className={`min-w-0 rounded-xl px-3 py-2 text-left transition ${calendarProvider === "microsoft" ? "bg-white text-indigo-900 shadow-sm ring-1 ring-indigo-200" : "text-slate-600 hover:bg-white"}`}
              >
                <span className="flex items-center justify-between gap-2 text-[11px] font-bold">
                  Outlook
                  {microsoftAccessToken && (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                  )}
                </span>
                <span className="mt-0.5 block text-[9px] font-medium text-slate-500">
                  {microsoftAccessToken ? "Connected" : "Not connected"}
                </span>
              </button>
              <button
                type="button"
                onClick={() => selectCalendarProvider("google")}
                className={`min-w-0 rounded-xl px-3 py-2 text-left transition ${calendarProvider === "google" ? "bg-white text-emerald-900 shadow-sm ring-1 ring-emerald-200" : "text-slate-600 hover:bg-white"}`}
              >
                <span className="flex items-center justify-between gap-2 text-[11px] font-bold">
                  Google
                  {googleAccessToken && (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                  )}
                </span>
                <span className="mt-0.5 block text-[9px] font-medium text-slate-500">
                  {googleAccessToken ? "Connected" : "Not connected"}
                </span>
              </button>
            </div>

            <div className="space-y-2.5 p-3">
              {calendarProvider === "microsoft" ? (
                !microsoftAccessToken ? (
                  <button
                    type="button"
                    onClick={connectMicrosoftCalendar}
                    disabled={isConnectingMicrosoft}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-700 px-3 py-2.5 text-xs font-bold text-white hover:bg-indigo-800 disabled:opacity-50"
                  >
                    {isConnectingMicrosoft && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    {isConnectingMicrosoft
                      ? "Connecting..."
                      : "Connect Outlook Calendar"}
                  </button>
                ) : (
                  <div className="flex items-end gap-2">
                    <div className="min-w-0 flex-1">
                      <Field label="Outlook calendar">
                        <select
                          className={INPUT_CLASS}
                          value={selectedMicrosoftCalendarId}
                          onChange={(event) => {
                            setSelectedMicrosoftCalendarId(event.target.value);
                            window.sessionStorage.setItem(
                              "clsConnectMicrosoftCalendarId",
                              event.target.value,
                            );
                          }}
                        >
                          {microsoftCalendars.map((calendar) => (
                            <option key={calendar.id} value={calendar.id}>
                              {calendar.summary}
                              {calendar.primary ? " (default)" : ""}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>
                    <button
                      type="button"
                      onClick={disconnectMicrosoftCalendar}
                      className="rounded-xl border border-slate-200 px-2.5 py-2.5 text-[10px] font-bold text-slate-600 hover:bg-slate-50"
                    >
                      Disconnect
                    </button>
                  </div>
                )
              ) : !googleAccessToken ? (
                <div className="space-y-2.5">
                  {!CLS_CONNECT_GOOGLE_CLIENT_ID && (
                    <Field label="Google OAuth client ID">
                      <input
                        className={INPUT_CLASS}
                        value={googleClientId}
                        onChange={(event) =>
                          setGoogleClientId(event.target.value)
                        }
                        placeholder="123456789.apps.googleusercontent.com"
                      />
                    </Field>
                  )}
                  <button
                    type="button"
                    onClick={connectGoogleCalendar}
                    disabled={isConnectingGoogle}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-700 px-3 py-2.5 text-xs font-bold text-white hover:bg-emerald-800 disabled:opacity-50"
                  >
                    {isConnectingGoogle && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    {isConnectingGoogle
                      ? "Connecting..."
                      : "Connect Google Calendar"}
                  </button>
                </div>
              ) : (
                <div className="space-y-2.5">
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2">
                    <Field label="Google calendar">
                      <select
                        className={INPUT_CLASS}
                        value={selectedGoogleCalendarId}
                        onChange={async (event) => {
                          const calendarId = event.target.value;
                          setSelectedGoogleCalendarId(calendarId);
                          setSelectedGoogleEventId("");
                          window.sessionStorage.setItem(
                            "clsConnectGoogleCalendarId",
                            calendarId,
                          );
                          try {
                            await loadConnectedGoogleEvents(
                              googleAccessToken,
                              calendarId,
                            );
                          } catch (error) {
                            toast.error(
                              error instanceof Error
                                ? error.message
                                : "Could not load Google Calendar events.",
                            );
                          }
                        }}
                      >
                        {googleCalendars.map((calendar) => (
                          <option key={calendar.id} value={calendar.id}>
                            {calendar.summary}
                            {calendar.primary ? " (default)" : ""}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <button
                      type="button"
                      onClick={disconnectGoogleCalendar}
                      className="rounded-xl border border-slate-200 px-2.5 py-2.5 text-[10px] font-bold text-slate-600 hover:bg-slate-50"
                    >
                      Disconnect
                    </button>
                  </div>
                  <Field label="Calendar event">
                    <select
                      className={INPUT_CLASS}
                      value={selectedGoogleEventId}
                      onChange={(event) =>
                        selectGoogleEvent(event.target.value)
                      }
                    >
                      <option value="">
                        Create a new Google Calendar event
                      </option>
                      {googleEvents.map((event) => (
                        <option key={event.id} value={event.id}>
                          {formatGoogleEventOption(event)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <button
                    type="button"
                    onClick={() =>
                      loadConnectedGoogleEvents(
                        googleAccessToken,
                        selectedGoogleCalendarId,
                      )
                    }
                    disabled={isLoadingGoogleEvents}
                    className="w-full rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] font-bold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                  >
                    {isLoadingGoogleEvents
                      ? "Refreshing events..."
                      : "Refresh events"}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {!isCompose && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900">
            This appointment is read-only. Open it in edit mode to change the
            Outlook entry. You can still send an outcome to Actionstep.
          </div>
        )}

        {alreadyLinked && !showMatterSection && !showListingSection && (
          <section className="order-1 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                  Linked matter
                </p>
                <p className="truncate text-sm font-bold text-emerald-950">
                  {[selectedMatter?.client?.fullName, selectedMatter?.client?.fileNumber]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                <p className="mt-0.5 text-xs text-emerald-800">
                  {[
                    fields.appearanceType,
                    fields.date,
                    [fields.startTime, fields.endTime].filter(Boolean).join("-"),
                    fields.court,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              <div className="flex shrink-0 gap-3">
                <button
                  type="button"
                  onClick={() => setShowMatterSection(true)}
                  className="text-[11px] font-bold text-emerald-800 underline"
                >
                  Change matter
                </button>
                <button
                  type="button"
                  onClick={() => setShowListingSection(true)}
                  className="text-[11px] font-bold text-emerald-800 underline"
                >
                  Edit listing
                </button>
              </div>
            </div>
          </section>
        )}

        <section
          className={`${isExistingCalendarEntry ? "order-2" : "order-1"} rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ${alreadyLinked && !showMatterSection ? "hidden" : ""}`}
        >
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold">1. Actionstep matter</h2>
              <p className="mt-0.5 text-[11px] text-slate-500">
                Search by client name or file reference.
              </p>
            </div>
            {(actionstep.isConnected || isDemoMode) && (
              <button
                type="button"
                onClick={disconnectMatterSource}
                className="flex items-center gap-1 text-[10px] font-bold text-slate-500 hover:text-red-600"
              >
                <LogOut className="h-3 w-3" />{" "}
                {isDemoMode ? "Exit demo" : "Disconnect"}
              </button>
            )}
          </div>

          {isDemoMode && (
            <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="rounded-full bg-amber-600 px-2 py-0.5 text-[10px] font-bold tracking-wider text-white">
                  DEMO MODE
                </span>
                <span className="text-[10px] font-semibold text-amber-900">
                  No Actionstep connection
                </span>
              </div>
              <p className="mt-2 text-xs leading-5 text-amber-900">
                Sample matters are used and simulated outcomes remain in this
                Outlook session only.
              </p>
            </div>
          )}

          {!actionstep.isConnected && !isDemoMode ? (
            <div className="space-y-3 rounded-xl border border-purple-100 bg-purple-50 p-3">
              <p className="text-xs leading-5 text-purple-900">
                Connect the same Actionstep account used by the Aid Planner. The
                resulting access token is kept only for this Outlook session.
              </p>
              {!ENV_CLIENT_ID && (
                <Field label="Actionstep client ID">
                  <input
                    className={INPUT_CLASS}
                    value={clientId}
                    onChange={(event) => setClientId(event.target.value)}
                  />
                </Field>
              )}
              {!ENV_CLIENT_SECRET && (
                <Field label="Actionstep client secret">
                  <input
                    type="password"
                    className={INPUT_CLASS}
                    value={clientSecret}
                    onChange={(event) => setClientSecret(event.target.value)}
                  />
                </Field>
              )}
              {actionstep.exchangeError && (
                <p className="text-xs text-red-700">
                  {actionstep.exchangeError}
                </p>
              )}
              <button
                type="button"
                onClick={connectActionstep}
                disabled={
                  !clientId ||
                  !clientSecret ||
                  actionstep.isExchanging ||
                  isOpeningActionstep
                }
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-purple-700 px-3 py-2.5 text-xs font-bold text-white hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {actionstep.isExchanging || isOpeningActionstep ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4" />
                )}
                {isOpeningActionstep
                  ? "Opening Actionstep..."
                  : "Connect to Actionstep"}
              </button>
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-purple-500">
                <span className="h-px flex-1 bg-purple-200" />
                or
                <span className="h-px flex-1 bg-purple-200" />
              </div>
              <button
                type="button"
                onClick={startDemoMode}
                className="w-full rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs font-bold text-amber-900 hover:bg-amber-100"
              >
                Test with sample matters
              </button>
            </div>
          ) : selectedMatter ? (
            <div className="overflow-hidden rounded-xl border border-green-200 bg-green-50">
              <div className="flex items-start justify-between gap-3">
                <div className="p-3">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-green-900">
                    <Link2 className="h-3.5 w-3.5" />
                    {selectedMatter.client?.fullName || "Selected matter"}
                  </div>
                  <p className="mt-1 text-[11px] text-green-800">
                    {selectedMatter.client?.fileNumber ||
                      `Actionstep ${selectedMatter.externalId}`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedMatter(null)}
                  className="p-3 text-[10px] font-bold text-green-800 underline"
                >
                  Change
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <div className="p-2">
                <form onSubmit={searchMatters} className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    <input
                      className={`${INPUT_CLASS} pl-9`}
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder="Client or file number"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={actionstep.isSearching}
                    className="rounded-xl bg-slate-900 px-3 text-xs font-bold text-white disabled:opacity-50"
                  >
                    {actionstep.isSearching ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Search"
                    )}
                  </button>
                </form>
                {!isDemoMode && actionstep.searchError && (
                  <p className="mt-2 px-1 text-xs text-red-700">
                    {actionstep.searchError}
                  </p>
                )}
                {isDemoMode &&
                  query.trim().length >= 2 &&
                  results.length === 0 && (
                    <p className="mt-2 px-1 text-xs text-slate-500">
                      No sample matters match that search.
                    </p>
                  )}
              </div>
              {results.length > 0 && (
                <div className="max-h-40 divide-y divide-slate-100 overflow-y-auto border-t border-slate-200">
                  {results.map((matter) => (
                    <button
                      key={matter.externalId}
                      type="button"
                      onClick={() => selectMatter(matter)}
                      className="block w-full px-3 py-2 text-left hover:bg-purple-50"
                    >
                      <div className="text-xs font-bold text-slate-900">
                        {matter.client?.fullName}
                      </div>
                      <div className="mt-0.5 text-[10px] text-slate-500">
                        {[matter.client?.fileNumber, matter.matter?.summary]
                          .filter(Boolean)
                          .join(" - ")}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        <section
          className={`${isExistingCalendarEntry ? "order-3" : "order-2"} space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ${alreadyLinked && !showListingSection ? "hidden" : ""}`}
        >
          <div>
            <h2 className="text-sm font-bold">2. Listing details</h2>
            <p className="mt-0.5 text-[11px] text-slate-500">
              Historical appointment dates and times are locked. Use Next court
              date to create the next appearance.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date">
              <input
                type="date"
                disabled={isExistingCalendarEntry}
                className={`${INPUT_CLASS} disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500`}
                value={fields.date}
                onChange={(event) => updateField("date", event.target.value)}
              />
            </Field>
            <Field label="Appearance type">
              <select
                className={INPUT_CLASS}
                value={fields.appearanceType}
                onChange={(event) =>
                  updateField("appearanceType", event.target.value)
                }
              >
                {APPEARANCE_TYPES.map((type) => (
                  <option key={type}>{type}</option>
                ))}
              </select>
            </Field>
            <Field label="Start time">
              <input
                type="time"
                disabled={isExistingCalendarEntry}
                className={`${INPUT_CLASS} disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500`}
                value={fields.startTime}
                onChange={(event) =>
                  updateField("startTime", event.target.value)
                }
              />
            </Field>
            <Field label="End time">
              <input
                type="time"
                disabled={isExistingCalendarEntry}
                className={`${INPUT_CLASS} disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500`}
                value={fields.endTime}
                onChange={(event) => updateField("endTime", event.target.value)}
              />
            </Field>
          </div>
          <Field label="Court">
            <div className="relative">
              <MapPin className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              {/* A list rather than free text, so one venue stops arriving as "Melb Mags",
                  "Melbourne Magistrates" and "MMC". A court already recorded that the list
                  does not carry stays as its own option, so nothing typed is lost. */}
              <select
                className={`${INPUT_CLASS} pl-9`}
                value={fields.court}
                onChange={(event) => updateField("court", event.target.value)}
              >
                <option value="">Select a court...</option>
                {withCurrentValue(COURT_OPTIONS, fields.court).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </Field>
          <Field label="Where listed">
            <input
              className={INPUT_CLASS}
              value={fields.listingLocation}
              onChange={(event) =>
                updateField("listingLocation", event.target.value)
              }
              placeholder="Courtroom 4, Criminal List, or Teams link"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Funding type">
              <select
                className={INPUT_CLASS}
                value={fields.fundingType}
                onChange={(event) =>
                  updateField("fundingType", event.target.value)
                }
              >
                {FUNDING_TYPES.map((type) => (
                  <option key={type}>{type}</option>
                ))}
              </select>
            </Field>
            {fields.fundingType === "Legal Aid" && (
              <Field label="Grant of aid number">
                <input
                  className={INPUT_CLASS}
                  value={fields.grantOfAidNumber}
                  onChange={(event) =>
                    updateField("grantOfAidNumber", event.target.value)
                  }
                  placeholder="VLA grant number"
                />
              </Field>
            )}
          </div>
          {/* Not rendered at all when the host draws the save button. `hidden` was tried and
              did nothing: the class list sets display:flex, which beats the hidden attribute,
              so two save buttons stayed on screen and an appointment was saved twice. */}
          {!portalRegisterSave && (
            <button
              type="button"
              onClick={saveCalendar}
              disabled={
                (!portalMode && !isStandaloneGoogle && !isCompose) ||
                isSavingCalendar
              }
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-purple-700 px-3 py-3 text-xs font-bold text-white hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSavingCalendar ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : calendarSaved ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <CalendarDays className="h-4 w-4" />
              )}
              {calendarSaved
                ? isStandaloneGoogle
                  ? "Google Calendar event saved"
                  : "Outlook appointment updated"
                : isStandaloneGoogle
                  ? "Save to Google Calendar"
                  : portalMode
                    ? portalEvent?.id
                      ? "Update selected Outlook appointment"
                      : "Create Outlook appointment"
                    : "Save to this Outlook appointment"}
            </button>
          )}
        </section>

        <details
          open={outcomeExpanded}
          onToggle={(event) => setOutcomeExpanded(event.currentTarget.open)}
          className={`${isExistingCalendarEntry ? "order-1" : "order-3"} group rounded-2xl border border-slate-200 bg-white shadow-sm`}
        >
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4">
            <div>
              <h2 className="text-sm font-bold">3. Outcome &amp; next steps</h2>
              <p className="mt-0.5 text-[11px] text-slate-500">
                Record the result and choose what should happen next.
              </p>
            </div>
            <ChevronDown className="h-4 w-4 shrink-0 text-slate-500 transition group-open:rotate-180" />
          </summary>
          <div className="flex flex-col gap-3 border-t border-slate-100 p-4">
            <p className="text-[11px] text-slate-500">
              {isDemoMode
                ? "This test creates a local preview only; nothing is sent to Actionstep."
                : "Nothing is written back to Actionstep until you confirm below."}
            </p>
            <Field label="Outcome">
              <textarea
                className={`${INPUT_CLASS} min-h-20 resize-y`}
                value={fields.outcome}
                onChange={(event) => updateField("outcome", event.target.value)}
                placeholder="Adjourned to 14 September 2026 for contest mention"
              />
            </Field>
            <Field label="Additional notes">
              <textarea
                className={`${INPUT_CLASS} min-h-20 resize-y`}
                value={fields.notes}
                onChange={(event) => updateField("notes", event.target.value)}
                placeholder="Orders, undertakings, next steps, or billing notes"
              />
            </Field>
            <details className="group/upload rounded-xl border border-slate-200 bg-slate-50">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-2 p-3 text-xs font-bold text-slate-800">
                <span className="flex items-center gap-2">
                  <UploadCloud className="h-4 w-4 text-purple-700" />
                  Upload material to Actionstep
                </span>
                <ChevronDown className="h-4 w-4 text-slate-500 transition group-open/upload:rotate-180" />
              </summary>
              <div className="space-y-3 border-t border-slate-200 p-3">
                <input
                  type="file"
                  multiple
                  onChange={(event) =>
                    setMaterialFiles(Array.from(event.target.files || []))
                  }
                  className="block w-full text-[11px] text-slate-600 file:mr-2 file:rounded-lg file:border-0 file:bg-purple-100 file:px-3 file:py-2 file:text-[11px] file:font-bold file:text-purple-800"
                />
                {materialFiles.length > 0 && (
                  <p className="text-[10px] leading-4 text-slate-600">
                    {materialFiles.map((file) => file.name).join(", ")}
                  </p>
                )}
                <button
                  type="button"
                  onClick={uploadMaterialsToActionstep}
                  disabled={
                    isDemoMode ||
                    !actionstep.isConnected ||
                    actionstep.isUploading ||
                    !materialFiles.length
                  }
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-purple-200 bg-white px-3 py-2.5 text-xs font-bold text-purple-800 hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {actionstep.isUploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UploadCloud className="h-4 w-4" />
                  )}
                  {actionstep.isUploading
                    ? "Uploading to Actionstep..."
                    : "Save files to this Actionstep matter"}
                </button>
                {isDemoMode && (
                  <p className="text-[10px] text-amber-800">
                    Uploads require a live Actionstep connection and are
                    disabled in Demo Mode.
                  </p>
                )}
                {actionstep.uploadError && (
                  <p className="text-[10px] text-red-700">
                    {actionstep.uploadError}
                  </p>
                )}
                {uploadedMaterials.length > 0 && (
                  <p className="text-[10px] font-semibold leading-4 text-green-700">
                    Saved: {uploadedMaterials.join(", ")}
                  </p>
                )}
              </div>
            </details>
            <details className="group/document rounded-xl border border-slate-200 bg-slate-50">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-2 p-3 text-xs font-bold text-slate-800">
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-purple-700" />
                  Generate a document
                </span>
                <ChevronDown className="h-4 w-4 text-slate-500 transition group-open/document:rotate-180" />
              </summary>
              <div className="grid gap-3 border-t border-slate-200 p-3">
                <Field label="Document type">
                  <select
                    className={INPUT_CLASS}
                    value={documentType}
                    onChange={(event) => setDocumentType(event.target.value)}
                  >
                    {DOCUMENT_TYPES.map((type) => (
                      <option key={type}>{type}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Instructions">
                  <textarea
                    className={`${INPUT_CLASS} min-h-16 resize-y`}
                    value={documentInstructions}
                    onChange={(event) =>
                      setDocumentInstructions(event.target.value)
                    }
                    placeholder="What the letter, file note, or document should include"
                  />
                </Field>
                <button
                  type="button"
                  onClick={() =>
                    submitPortalRequest(
                      "document",
                      documentInstructions || fields.notes,
                      documentType,
                    )
                  }
                  disabled={isSubmittingPortalRequest}
                  className="rounded-xl border border-purple-200 bg-purple-50 px-3 py-2.5 text-xs font-bold text-purple-800 hover:bg-purple-100 disabled:opacity-50"
                >
                  {isDemoMode
                    ? "Simulate document request"
                    : "Send document request to LACW portal"}
                </button>
              </div>
            </details>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={fields.matterToBeBriefed}
                  onChange={(event) =>
                    updateField("matterToBeBriefed", event.target.checked)
                  }
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-purple-700 focus:ring-purple-500"
                />
                <span>
                  <span className="block text-xs font-bold text-slate-900">
                    Matter to be briefed
                  </span>
                  <span className="mt-0.5 block text-[11px] leading-4 text-slate-500">
                    Request available counsel or prepare the counsel backsheet.
                  </span>
                </span>
              </label>

              {fields.matterToBeBriefed && (
                <div className="mt-3 space-y-3 border-t border-slate-200 pt-3">
                  <Field label="Request a list of counsel">
                    <select
                      className={INPUT_CLASS}
                      value={fields.counselListMethod}
                      onChange={(event) =>
                        updateField("counselListMethod", event.target.value)
                      }
                    >
                      <option value="email_clerk">Email a clerk myself</option>
                      <option value="task_team">Task a paralegal or admin</option>
                    </select>
                  </Field>

                  {fields.counselListMethod === "email_clerk" ? (
                    <>
                      <Field label="Clerk email">
                        <input
                          type="email"
                          className={INPUT_CLASS}
                          value={fields.clerkEmail}
                          onChange={(event) =>
                            updateField("clerkEmail", event.target.value)
                          }
                          placeholder="clerks@example.com.au"
                        />
                      </Field>
                      <button
                        type="button"
                        onClick={emailClerkForCounsel}
                        className="w-full rounded-xl border border-purple-200 bg-white px-3 py-2.5 text-xs font-bold text-purple-800 hover:bg-purple-50"
                      >
                        Open email requesting counsel list
                      </button>
                    </>
                  ) : (
                    <>
                      <Field label="Assign request to">
                        <select
                          className={INPUT_CLASS}
                          value={fields.briefingAssigneeRole}
                          onChange={(event) =>
                            updateField(
                              "briefingAssigneeRole",
                              event.target.value,
                            )
                          }
                        >
                          <option>Paralegal</option>
                          <option>Admin</option>
                        </select>
                      </Field>
                      <Field label="Additional instructions">
                        <textarea
                          className={`${INPUT_CLASS} min-h-16 resize-y`}
                          value={fields.briefingNotes}
                          onChange={(event) =>
                            updateField("briefingNotes", event.target.value)
                          }
                          placeholder="Preferred list, counsel experience, fee limit, or response deadline"
                        />
                      </Field>
                      <button
                        type="button"
                        onClick={taskCounselListRequest}
                        disabled={isSubmittingPortalRequest}
                        className="w-full rounded-xl border border-purple-200 bg-white px-3 py-2.5 text-xs font-bold text-purple-800 hover:bg-purple-50 disabled:opacity-50"
                      >
                        {isDemoMode
                          ? "Simulate counsel-list task"
                          : "Send counsel-list task to LACW portal"}
                      </button>
                    </>
                  )}

                  <button
                    type="button"
                    onClick={openBacksheetTool}
                    className="w-full rounded-xl bg-slate-900 px-3 py-3 text-xs font-bold text-white hover:bg-slate-800"
                  >
                    Prepare Backsheet to Counsel
                  </button>
                </div>
              )}
            </div>
            <div className="grid gap-2">
              {isCompose && (
                <button
                  type="button"
                  onClick={saveCalendar}
                  disabled={isSavingCalendar}
                  className="rounded-xl border border-purple-200 bg-purple-50 px-3 py-2.5 text-xs font-bold text-purple-800 hover:bg-purple-100"
                >
                  Update outcome in Outlook
                </button>
              )}
              <button
                type="button"
                onClick={saveOutcomeToActionstep}
                disabled={
                  (!actionstep.isConnected && !isDemoMode) ||
                  actionstep.isSaving ||
                  outcomeSaved
                }
                className="flex items-center justify-center gap-2 rounded-xl bg-green-700 px-3 py-3 text-xs font-bold text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {actionstep.isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : outcomeSaved ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <Link2 className="h-4 w-4" />
                )}
                {outcomeSaved
                  ? isDemoMode
                    ? "Demo outcome saved"
                    : "Outcome saved to Actionstep"
                  : isDemoMode
                    ? "Simulate saving outcome"
                    : "Confirm and save outcome to Actionstep"}
              </button>
              {portalMode ? (
                <div className={`flex items-center gap-2 rounded-xl border px-3 py-3 text-xs font-bold ${billingQueued ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-blue-200 bg-blue-50 text-blue-800"}`}>
                  {billingQueued ? <CheckCircle2 className="h-4 w-4" /> : <CalendarDays className="h-4 w-4" />}
                  {billingQueued ? "Automatically added to billing review" : "Saving this appearance will also update Admin Billing"}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={sendToBillingIntake}
                  disabled={isSendingToBilling || billingQueued}
                  className="flex items-center justify-center gap-2 rounded-xl border border-blue-300 bg-blue-50 px-3 py-3 text-xs font-bold text-blue-800 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSendingToBilling ? <Loader2 className="h-4 w-4 animate-spin" /> : billingQueued ? <CheckCircle2 className="h-4 w-4" /> : <CalendarDays className="h-4 w-4" />}
                  {billingQueued ? "Added to LACW billing intake" : isDemoMode ? "Simulate LACW billing intake" : "Send to LACW billing intake"}
                </button>
              )}
              {isDemoMode && demoSavedNote && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800">
                    Simulated Actionstep file note
                  </div>
                  <pre className="mt-2 whitespace-pre-wrap font-sans text-[11px] leading-5 text-amber-950">
                    {demoSavedNote}
                  </pre>
                </div>
              )}
            </div>
            <div className="order-first space-y-3">
              <div className="border-t border-slate-200 pt-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Choose next step
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {FOLLOW_UP_TYPES.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      setFollowUpType(id);
                      setPortalRequestReceipt(null);
                    }}
                    className={`rounded-xl border p-2 text-center text-[10px] font-bold transition ${
                      followUpType === id
                        ? "border-purple-500 bg-purple-50 text-purple-900"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <Icon className="mx-auto mb-1 h-4 w-4" />
                    {label}
                  </button>
                ))}
              </div>

              {followUpType === "next_court_date" && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="New court date">
                      <input
                        type="date"
                        className={INPUT_CLASS}
                        value={fields.nextCourtDate}
                        onChange={(event) =>
                          updateField("nextCourtDate", event.target.value)
                        }
                      />
                    </Field>
                    <Field label="Start time">
                      <input
                        type="time"
                        className={INPUT_CLASS}
                        value={fields.nextCourtTime}
                        onChange={(event) =>
                          updateField("nextCourtTime", event.target.value)
                        }
                      />
                    </Field>
                  </div>
                  <Field label="Next appearance type">
                    <select
                      className={INPUT_CLASS}
                      value={fields.nextAppearanceType}
                      onChange={(event) =>
                        updateField("nextAppearanceType", event.target.value)
                      }
                    >
                      {APPEARANCE_TYPES.map((type) => (
                        <option key={type}>{type}</option>
                      ))}
                    </select>
                  </Field>
                  <button
                    type="button"
                    onClick={createNextCourtAppointment}
                    disabled={isSavingNextCourt || nextCourtSaved}
                    className="w-full rounded-xl bg-purple-700 px-3 py-3 text-xs font-bold text-white hover:bg-purple-800 disabled:opacity-50"
                  >
                    {isSavingNextCourt
                      ? "Saving next court date..."
                      : nextCourtSaved
                        ? microsoftAccessToken && selectedMicrosoftCalendarId
                          ? "Next Outlook event created"
                          : "Separate appointment opened"
                        : isStandaloneGoogle
                          ? "Create next Google event"
                          : microsoftAccessToken && selectedMicrosoftCalendarId
                            ? "Create next Outlook event"
                            : "Open separate appointment"}
                  </button>
                  <p className="text-[10px] leading-4 text-slate-500">
                    {isStandaloneGoogle ? (
                      "The current appearance always remains on its original date. A separate event is created for the next appearance."
                    ) : microsoftAccessToken && selectedMicrosoftCalendarId ? (
                      "The current appearance always remains on its original date. A separate Outlook event is created automatically for the next appearance."
                    ) : (
                      <>
                        The current appearance always remains on its original
                        date. Outlook opens a separate appointment for the next
                        appearance; select <strong>Save &amp; Close</strong>{" "}
                        there.
                      </>
                    )}
                  </p>
                </div>
              )}

              {followUpType === "action_item" && (
                <div className="space-y-3">
                  <Field label="Action requested">
                    <textarea
                      className={`${INPUT_CLASS} min-h-20 resize-y`}
                      value={actionItem}
                      onChange={(event) => setActionItem(event.target.value)}
                      placeholder="Describe the action required, responsible person, and due date"
                    />
                  </Field>
                  <Field label="Priority">
                    <select
                      className={INPUT_CLASS}
                      value={actionPriority}
                      onChange={(event) =>
                        setActionPriority(event.target.value)
                      }
                    >
                      {["Normal", "High", "Urgent"].map((priority) => (
                        <option key={priority}>{priority}</option>
                      ))}
                    </select>
                  </Field>
                  <button
                    type="button"
                    onClick={() =>
                      submitPortalRequest("action_item", actionItem)
                    }
                    disabled={isSubmittingPortalRequest}
                    className="w-full rounded-xl bg-slate-900 px-3 py-3 text-xs font-bold text-white hover:bg-slate-800 disabled:opacity-50"
                  >
                    {isDemoMode
                      ? "Simulate action request"
                      : "Send action request to LACW portal"}
                  </button>
                </div>
              )}

              {followUpType === "close_file" && (
                <div className="space-y-3">
                  <Field label="File outcome">
                    <textarea
                      className={`${INPUT_CLASS} min-h-24 resize-y`}
                      value={fileOutcome}
                      onChange={(event) => setFileOutcome(event.target.value)}
                      placeholder="Final result, orders, advice given, and reason the file can be closed"
                    />
                  </Field>
                  <button
                    type="button"
                    onClick={() =>
                      submitPortalRequest("close_file", fileOutcome)
                    }
                    disabled={isSubmittingPortalRequest}
                    className="w-full rounded-xl bg-red-700 px-3 py-3 text-xs font-bold text-white hover:bg-red-800 disabled:opacity-50"
                  >
                    {isDemoMode
                      ? "Simulate file closure request"
                      : "Request file closure through LACW portal"}
                  </button>
                </div>
              )}

              {portalRequestReceipt && (
                <div
                  className={`rounded-xl border p-3 text-xs font-semibold ${
                    portalRequestReceipt.simulated
                      ? "border-amber-200 bg-amber-50 text-amber-900"
                      : "border-green-200 bg-green-50 text-green-900"
                  }`}
                >
                  {portalRequestReceipt.simulated && (
                    <span className="mr-1 font-extrabold">DEMO:</span>
                  )}
                  {portalRequestReceipt.label}
                  {portalRequestReceipt.id
                    ? ` (${portalRequestReceipt.id})`
                    : ""}
                </div>
              )}
            </div>
          </div>
        </details>
      </div>
    </main>
  );
}
